// ============================================================================
// AuthRequiredScreen — ЕДИНЫЙ экран «нужен вход» для витрин/приложений.
//
// Зачем: гость видит содержимое (сайт/PWA), но действие требует учётной
// записи. Экран объясняет это и ведёт на вход/регистрацию — мастер заявки
// ДО входа не открывается.
//
// Оформление: та же карточная школа, что у витрины маркетплейса (крупные
// касания, скругления, никаких таблиц и окон платформы).
// ============================================================================

import type { ReactNode } from 'react';
import { LockKeyhole, LogIn, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AuthRequiredScreenProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction: () => void;
  /** Дополнительная плашка (например, «черновик сохранён»). */
  note?: ReactNode;
  /** Явная ошибка входа/проверки (если есть). */
  error?: string | null;
}

export function AuthRequiredScreen({
  title,
  description,
  actionLabel = 'Войти или зарегистрироваться',
  onAction,
  note,
  error,
}: AuthRequiredScreenProps) {
  return (
    <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-sm sm:p-7">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <LockKeyhole className="h-6 w-6" />
        </div>

        <h1 className="mt-4 text-xl font-semibold leading-tight sm:text-2xl">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>

        {note && (
          <div className="mt-4 rounded-2xl border bg-background/70 px-3 py-2.5 text-xs leading-5 text-muted-foreground">
            {note}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs leading-5 text-destructive">
            {error}
          </div>
        )}

        <Button
          type="button"
          size="lg"
          className="mt-5 h-12 w-full rounded-xl text-base shadow-sm"
          onClick={onAction}
        >
          <LogIn className="mr-2 h-5 w-5" />
          {actionLabel}
        </Button>

        <div className="mt-3 flex items-start gap-2 text-[11px] leading-4 text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" />
          <span>
            Регистрация доступна на странице входа. После входа вы вернётесь к заявке —
            введённые данные сохранятся.
          </span>
        </div>
      </div>
    </div>
  );
}
